// src/stores/projectContext.js
// PURPOSE: Single source of truth for project context across the entire app
// All components read from here instead of passing props or using localStorage directly

import { defineStore } from 'pinia'

export const useProjectStore = defineStore('projectContext', {
  // ============================================
  // STATE: The actual data
  // ============================================
  state: () => ({
    // The currently active project (null = no project context)
    activeProject: null,
    
    // UI state for transitions
    isTransitioning: false,
    
    // Track which candidates are being processed in OTHER projects
    // Format: { candidate_id: { project_id, project_name, status } }
    candidateProjectConflicts: {},
    
    // User role for context-aware behavior
    userRole: null, // 'agent' | 'client'
    _userId: null,  //  internal, for localStorage namespacing

  }),

  // ============================================
  // GETTERS: Computed properties from state
  // ============================================
  getters: {
    // Quick boolean check - use this in v-if statements
    hasActiveProject: (state) => state.activeProject !== null,
    
    // Get project ID safely (returns null if no project)
    projectId: (state) => state.activeProject?.project_id || null,
    
    // Get project name for display
    projectName: (state) => state.activeProject?.project_name || '',
    
    // Check if a specific candidate has conflicts
    getCandidateConflict: (state) => (candidateId) => {
      return state.candidateProjectConflicts[candidateId] || null
    },
  },

  // ============================================
  // ACTIONS: Methods that modify state
  // ============================================
  actions: {
    /**
     * Set the active project context
     * Call this when: agent clicks into a project, client selects project
     */
    setProject(project) {
      // Validate input
      if (!project || !project.project_id) {
        console.warn('⚠️ ProjectStore: Invalid project passed to setProject')
        return false
      }

      // If switching projects, trigger transition state
      const isSwitching = this.activeProject && 
                          this.activeProject.project_id !== project.project_id
      
      if (isSwitching) {
        this.isTransitioning = true
        console.log('🔄 ProjectStore: Switching from', 
          this.activeProject.project_name, 'to', project.project_name)
      }

      // Update state
      this.activeProject = { ...project }
      
      // Persist to localStorage (backup for page refresh)
      this._persistToStorage()
      
      // Clear transition after short delay (allows UI to show loading)
      if (isSwitching) {
        setTimeout(() => {
          this.isTransitioning = false
        }, 300) // 300ms = enough for loading indicator to flash
      }

      console.log('✅ ProjectStore: Active project set to', project.project_name)
      return true
    },

    /**
     * Clear project context (exit project mode)
     */
    clearProject() {
      console.log('🚪 ProjectStore: Clearing project context')
      this.activeProject = null
      this.isTransitioning = false
      
      // Clear namespaced key if we have userId
      if (this._userId) {
        localStorage.removeItem(`projectContext_${this._userId}`)
      }
      // Also clear legacy key (for migration)
      localStorage.removeItem('projectContext')
      localStorage.removeItem('selectedProject')  //  legacy

    },

    /**
     * Set user role (agent or client)
     */
    setUserRole(role) {
      if (!['agent', 'client', 'admin'].includes(role)) {
        console.warn('⚠️ ProjectStore: Invalid role:', role)
        return
      }
      this.userRole = role
    },

    /**
     * Register a candidate conflict (being screened elsewhere)
     * Call this when loading candidate data that shows cross-project status
     */
    registerConflict(candidateId, conflictData) {
      this.candidateProjectConflicts[candidateId] = {
        project_id: conflictData.project_id,
        project_name: conflictData.project_name,
        status: conflictData.status, // e.g., 'screening_in_progress', 'shortlisted'
        timestamp: Date.now()
      }
    },

    /**
     * Clear a specific conflict (candidate released from other project)
     */
    clearConflict(candidateId) {
      delete this.candidateProjectConflicts[candidateId]
    },

    /**
     * Initialize from localStorage on app start  
     * * @param {string} userId - Current user's ID for namespacing
     */
    initFromStorage(userId) {
      if (!userId) {
        console.warn('⚠️ ProjectStore: No userId provided, skipping storage restore')
        return
      }
      
      this._userId = userId  // Store for later persistence
      
      try {
        const stored = localStorage.getItem(`projectContext_${userId}`)
        if (stored) {
          const parsed = JSON.parse(stored)
          if (parsed.project_id && parsed.project_name) {
            this.activeProject = parsed
            console.log('📦 ProjectStore: Restored from storage:', parsed.project_name)
          }
        }
      } catch (e) {
        console.warn('⚠️ ProjectStore: Failed to restore from storage:', e)
        localStorage.removeItem(`projectContext_${userId}`)
      }
    },

    /**
     * Internal: Persist to localStorage
     */
    _persistToStorage() {
      if (this.activeProject && this._userId) {
        localStorage.setItem(`projectContext_${this._userId}`, JSON.stringify(this.activeProject))
      }
    }
  }
})